/**
 * TitleModal - Name your piece before saving
 */

import { useState, useEffect, useRef } from 'react';

interface TitleModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
  isSaving?: boolean;
}

export function TitleModal({ isOpen, onCancel, onSave, isSaving = false }: TitleModalProps) {
  const [title, setTitle] = useState('');
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setIsAnimatingOut(false);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Detect keyboard (viewport height change)
  useEffect(() => {
    if (!isOpen) return;

    const initialHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleResize = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const diff = initialHeight - currentHeight;
      setIsKeyboardOpen(diff > 100);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleCancel = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onCancel();
      setIsAnimatingOut(false);
    }, 200);
  };

  const handleSave = () => {
    onSave(title.trim() || 'Untitled');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  if (!isOpen && !isAnimatingOut) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col justify-end transition-all duration-200 ${
        isAnimatingOut ? 'bg-black/0' : 'bg-black/40'
      }`}
      style={{ backdropFilter: isAnimatingOut ? 'blur(0px)' : 'blur(4px)' }}
      onClick={handleBackdropClick}
    >
      {/* Modal Card */}
      <div
        className={`bg-cream-100 rounded-t-2xl transition-all duration-300 ease-out ${
          isAnimatingOut 
            ? 'translate-y-full opacity-0' 
            : 'translate-y-0 opacity-100'
        }`}
        style={{
          animation: !isAnimatingOut ? 'slideUpModal 0.3s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
        }}
      >
        {/* Content */}
        <div className="px-5 pt-6 pb-5">
          {/* Handle bar */}
          <div className="w-10 h-1 bg-charcoal-200 rounded-full mx-auto mb-5" />
          
          <label className="block text-base font-semibold text-charcoal-800 mb-3">
            Name this piece
          </label>
          
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Untitled"
            className="w-full px-4 py-3.5 text-base bg-charcoal-50 border border-charcoal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-charcoal-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
          />
        </div>

        {/* 50/50 Edge-to-edge buttons - 80px default, 70px when keyboard open */}
        <div 
          className="flex border-t border-charcoal-100"
          style={{ paddingBottom: isKeyboardOpen ? 0 : 'env(safe-area-inset-bottom)' }}
        >
          {/* Cancel - Left 50% */}
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className={`flex-1 flex items-center justify-center bg-cream-100 text-charcoal-600 text-base font-semibold border-r border-charcoal-100 active:bg-charcoal-50 transition-all ${
              isKeyboardOpen ? 'h-[70px]' : 'h-20'
            }`}
          >
            Cancel
          </button>

          {/* Save - Right 50% */}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`flex-1 flex items-center justify-center bg-teal-500 text-white text-base font-semibold active:bg-teal-600 transition-all ${
              isKeyboardOpen ? 'h-[70px]' : 'h-20'
            } ${isSaving ? 'opacity-80 cursor-wait' : ''}`}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUpModal {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default TitleModal;
